// Change #100326 - every WhatsApp / messaging API call in one place. Used by the inbox
// (pages/MessagingService), the Customer / Contact / Job WhatsApp tabs and Settings > WhatsApp.
//
// All calls go through FetchAPI (JWT from localStorage). The backend answers HTTP 200 with
// { status, message, ... }; `ok(res)` is the one test for success.
import { FetchAPI } from "../../helpers/APIdata/fetchAPI";

export const ok = (res) => Number(res?.status) === 200;
export const errorText = (res, fallback = "Something went wrong") =>
  res?.message || res?.error || fallback;

// ---- conversations
export const searchConversations = (filters) =>
  FetchAPI("/messagingconversation/search", "POST", [{ messagingconversation_data: filters }]);
export const getConversation = (id) => FetchAPI(`/messagingconversation/data/${id}`, "GET");
export const createConversation = (payload) => FetchAPI("/messagingconversation/create", "POST", [payload]);
export const updateConversation = (payload) => FetchAPI("/messagingconversation/update", "PUT", [payload]);
export const closeConversation = (id, sendCloseTemplate = true) =>
  FetchAPI("/messagingconversation/close", "POST", [{ Id: id, FlagSendCloseTemplate: sendCloseTemplate ? 1 : 0 }]);
export const markConversationRead = (id) => FetchAPI("/messagingconversation/markread", "POST", [{ Id: id }]);
export const markConversationUnread = (id) => FetchAPI("/messagingconversation/markunread", "POST", [{ Id: id }]);
export const aiSuggest = (id) => FetchAPI("/messagingconversation/aisuggest", "POST", [{ Id: id }]);
export const exportConversation = (id, withNotes = false) =>
  FetchAPI(`/messagingconversation/export/${id}${withNotes ? "?notes=1" : ""}`, "GET");

// ---- messages
export const getMessages = (conversationId, beforeId) =>
  FetchAPI("/messagingconversationmessage/search", "POST", [
    { MessagingConversation_Id: conversationId, ...(beforeId ? { BeforeId: beforeId } : {}) },
  ]);
export const sendText = (conversationId, message, aiGenerated = false) =>
  FetchAPI("/messagingconversationmessage/send", "POST", [
    { MessagingConversation_Id: conversationId, Message: message, FlagAiGenerated: aiGenerated ? 1 : 0 },
  ]);
export const sendTemplate = (conversationId, templateId, variables) =>
  FetchAPI("/messagingconversationmessage/start", "POST", [
    { MessagingConversation_Id: conversationId, MessagingConversationTemplate_Id: templateId, variables },
  ]);
export const sendMedia = (payload) => FetchAPI("/messagingconversationmessage/media", "POST", [payload]);
export const addNote = (conversationId, message) =>
  FetchAPI("/messagingconversationmessage/note", "POST", [{ MessagingConversation_Id: conversationId, Message: message }]);
export const retryMessage = (id) => FetchAPI("/messagingconversationmessage/retry", "POST", [{ Id: id }]);
export const deleteNote = (id) => FetchAPI("/messagingconversationmessage/delete", "DELETE", [{ Id: id }]);

// ---- participants
export const removeParticipant = (id) => FetchAPI("/messagingconversationparticipant/remove", "POST", [{ Id: id }]);

// ---- channels
export const listChannels = () => FetchAPI("/messagingchannel/list", "GET");
export const createChannel = (payload) => FetchAPI("/messagingchannel/create", "POST", [payload]);
export const updateChannel = (payload) => FetchAPI("/messagingchannel/update", "PUT", [payload]);
export const deleteChannel = (id) => FetchAPI("/messagingchannel/delete", "DELETE", [{ Id: id }]);
export const testChannel = (id) => FetchAPI("/messagingchannel/test", "POST", [{ Id: id }]);

// ---- templates
export const listTemplates = () => FetchAPI("/messagingconversationtemplate/list", "GET");
export const searchTemplates = (channelId, approvedOnly = true) =>
  FetchAPI("/messagingconversationtemplate/search", "POST", [
    { MessagingChannel_Id: channelId, FlagApprovedOnly: approvedOnly ? 1 : 0 },
  ]);
export const createTemplate = (payload) => FetchAPI("/messagingconversationtemplate/create", "POST", [payload]);
export const updateTemplate = (payload) => FetchAPI("/messagingconversationtemplate/update", "PUT", [payload]);
export const deleteTemplate = (id) => FetchAPI("/messagingconversationtemplate/delete", "DELETE", [{ Id: id }]);
export const syncTemplates = (channelId) =>
  FetchAPI("/messagingconversationtemplate/sync", "POST", [channelId ? { MessagingChannel_Id: channelId } : {}]);

// ---- quick replies
export const listQuickReplies = (channelId, activeOnly = false) => {
  const q = [];
  if (channelId) q.push(`MessagingChannel_Id=${channelId}`);
  if (activeOnly) q.push("active=1");
  return FetchAPI(`/messagingconversationquickreply/list${q.length ? "?" + q.join("&") : ""}`, "GET");
};
export const createQuickReply = (payload) => FetchAPI("/messagingconversationquickreply/create", "POST", [payload]);
export const updateQuickReply = (payload) => FetchAPI("/messagingconversationquickreply/update", "PUT", [payload]);
export const deleteQuickReply = (id) => FetchAPI("/messagingconversationquickreply/delete", "DELETE", [{ Id: id }]);

// ---- consent
export const searchConsent = (filters = {}) => FetchAPI("/messagingcontactconsent/search", "POST", [filters]);
export const setConsent = (payload) => FetchAPI("/messagingcontactconsent/set", "POST", [payload]);

// ---- notifications
export const listNotificationRules = () => FetchAPI("/messagingnotificationrule/list", "GET");
export const createNotificationRule = (payload) => FetchAPI("/messagingnotificationrule/create", "POST", [payload]);
export const updateNotificationRule = (payload) => FetchAPI("/messagingnotificationrule/update", "PUT", [payload]);
export const deleteNotificationRule = (id) => FetchAPI("/messagingnotificationrule/delete", "DELETE", [{ Id: id }]);
export const notificationLog = (filters = {}) => {
  const q = Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  return FetchAPI(`/messagingnotificationrule/log${q.length ? "?" + q.join("&") : ""}`, "GET");
};
export const sendNotificationNow = (eventCode, entityId, force = false) =>
  FetchAPI("/messagingnotificationrule/send", "POST", [{ EventCode: eventCode, Entity_Id: entityId, FlagForce: force ? 1 : 0 }]);

// ---- helpers

/** Read a File as a data URI (what the media endpoint expects). */
export const fileToDataUri = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** Download a base64 PDF the API returned. */
export const downloadBase64Pdf = (base64, fileName) => {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "conversation.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// ---- lookups
export const getNumbers = (link) => FetchAPI("/messagingconversation/numbers", "POST", [link]);
export const listStaff = () => FetchAPI("/staff/list", "GET");
/** Site-wide search (customers, contacts, jobs...). Items carry ZGroup, Id, ObjectData. */
export const siteSearch = (term) => FetchAPI(`/search/search/${encodeURIComponent(term)}`, "GET");
